import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { es, type Dictionary } from './dictionaries/es';
import { DEFAULT_LOCALE, detectLocale, isLocale, localeMeta, type Locale } from './locales';

export type { Dictionary };
export * from './locales';

const STORAGE_KEY = 'caffeina_locale';

/**
 * Only Spanish ships in the main bundle. Vite emits the other fourteen as
 * their own chunks from this import, fetched the first time a visitor lands on
 * or picks that language.
 *
 * Bundling all fifteen would have added tens of kilobytes to first paint for
 * every local customer, which is the opposite of what the page-speed pass was
 * for. The cost is that a non-Spanish visitor sees Spanish for the length of
 * one same-origin request — no blank screen, and the swap is imperceptible on
 * a warm cache.
 */
function load(locale: Locale): Promise<{ default: Dictionary }> {
  return import(`./dictionaries/${locale}.ts`) as Promise<{ default: Dictionary }>;
}

/** Reads the saved choice. Storage throws in private mode and inside sandboxes. */
function readStored(): Locale | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** A returning visitor's own choice always beats the browser's setting. */
function initialLocale(): Locale {
  return readStored() ?? detectLocale();
}

interface I18nValue {
  t: Dictionary;
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

/**
 * Spanish by default so a component rendered outside the provider — which is
 * how the unit tests mount screens — still has real copy rather than blank
 * strings or a thrown error.
 */
const I18nContext = createContext<I18nValue>({
  t: es,
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [t, setT] = useState<Dictionary>(es);

  useEffect(() => {
    if (locale === DEFAULT_LOCALE) {
      setT(es);
      return;
    }

    let alive = true;
    void load(locale)
      .then((mod) => {
        if (alive) setT(mod.default);
      })
      .catch(() => {
        // A failed chunk leaves Spanish in place, which is readable. Falling
        // back to blank keys would not be.
      });

    return () => {
      alive = false;
    };
  }, [locale]);

  // Screen readers announce in the wrong accent without lang, and Arabic and
  // Urdu lay out backwards without dir.
  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = localeMeta(locale).dir;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private mode: the choice holds for this page view and no longer.
    }
    setLocaleState(next);
  }, []);

  const value = useMemo(() => ({ t, locale, setLocale }), [t, locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}

/** Shorthand for the common case of only needing the strings. */
export function useT(): Dictionary {
  return useContext(I18nContext).t;
}
